import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import DialogModal from 'components/DialogModal/DialogModal';
import { useNotifications } from 'components/Notifications/hooks/useNotifications';

import {
  selectNotificationsLoading,
  selectReadNotifications
} from 'features/notifications/notificationsSelectors';
import { getReadNotifications } from 'features/notifications/notificationsThunks';

import { useInfiniteScrollPagination } from 'hooks/useInfiniteScrollPagination';
import usePageviewEvent from 'hooks/usePageViewEvent';
import { useAppSelector } from 'hooks/useTypedRedux';

import { NotificationsList } from 'types/notifications';

import { DialogModalTypes, GTMEvents } from 'constants/enums';

import NotificationCard from '../NotificationCard/NotificationCard';
import styles from './NotificationsArchive.module.scss';

const NotificationsArchive = () => {
  const { t } = useTranslation();
  const readNotifications = useAppSelector(selectReadNotifications);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] =
    useState<boolean>(false);

  const isNotificationsLoading = useAppSelector(selectNotificationsLoading);

  const { deleteReadNotification, deleteAllReadNotifications } =
    useNotifications();

  const { lastElementRef } = useInfiniteScrollPagination<NotificationsList>({
    loadItemsThunk: getReadNotifications,
    errorMessage: t('error.message.loadingNotifications')
  });

  usePageviewEvent(GTMEvents.NotificationViewArchive);

  const handleDeleteConfirmModalOpen = () => {
    setIsDeleteConfirmModalOpen(true);
  };

  const handleDeleteConfirmModalClose = () => {
    setIsDeleteConfirmModalOpen(false);
  };

  const handleDeleteAllReadNotifications = () => {
    deleteAllReadNotifications();
    handleDeleteConfirmModalClose();
  };

  const isEmpty = !readNotifications?.length;

  if (isNotificationsLoading && isEmpty) {
    return <aside>{t('common.loading')}</aside>;
  }

  return (
    <>
      <aside>
        <div className={styles.top}>
          <div className={styles.title}>{t('notification.archive')}</div>
          {!isEmpty && (
            <button
              onClick={handleDeleteConfirmModalOpen}
              className={styles.deleteBtn}
            >
              {t('common.deleteAll')}
            </button>
          )}
        </div>

        {isEmpty ? (
          <div className={styles.empty}>{t('common.empty')}</div>
        ) : (
          <div className={styles.list}>
            {readNotifications.map((notification, index) => {
              const isLastElement = readNotifications.length === index + 1;
              return (
                <div
                  key={notification.id}
                  ref={isLastElement ? lastElementRef : null}
                >
                  <NotificationCard
                    notification={notification}
                    onReadNotification={deleteReadNotification}
                  />
                </div>
              );
            })}
          </div>
        )}
      </aside>
      {isDeleteConfirmModalOpen && (
        <DialogModal
          onClose={handleDeleteConfirmModalClose}
          isOpen={isDeleteConfirmModalOpen}
          title={t('common.delete')}
          description={t('notification.areYouSureDelete')}
          type={DialogModalTypes.Delete}
          onConfirm={handleDeleteAllReadNotifications}
        />
      )}
    </>
  );
};

export default NotificationsArchive;
